import React, { useEffect, useMemo, useState } from 'react'

import { ArrowDownTrayIcon, DocumentDuplicateIcon, LinkIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { format as formatDate } from 'date-fns'
import _find from 'lodash/find'
import { useRouter } from 'next/router'
import NumberFormat from 'react-number-format'
import ReactTooltip from 'react-tooltip'

import { DealAudit } from '../DealAudit'
import AppLinkSection from '../DealSummary/AppLinkSection'
import { AvmReportModal } from '../DealSummary/AvmReportModal/index.deprecated'
import { PullCreditModal } from '../DealSummary/PullCreditModal'
import { DocDrawModal } from '../SidePanel/DocDrawModal'
import { ViewTradelinesModal } from '../application/ViewTradelinesModal'
import { ArrowTopRightOnSquare } from '@/assets/icons/ArrowTopRightOnSquare'
import {
  setAvmReportModalOpen,
  setPullCreditModalAction,
  setPullCreditModalOpen,
  setPullCreditModalUser,
  setViewTradelinesModalOpen,
} from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import CustomNumberFormat from '@/components/CustomFields/components/CustomNumberFormat'
import DropdownButton from '@/components/DropdownButton'
import { PROPERTY_USAGES } from '@/constants/globals'
import { DownloadsSection } from '@/features/DealSummary/DownloadsSection'
import { StageSelector } from '@/features/DealSummaryBar/StageSelector'
import { Application } from '@/graphql/generated'
import { useIsLockedOffer } from '@/graphql/services/application'
import {
  useGetApplicationCalcsQuery,
  useGetApplicationOfferCalcsQuery,
  useGetApplicationValuationsCalcsQuery,
} from '@/graphql/services/applicationCalcs'
import { useGetOverrideByApplicationQuery } from '@/services/override'
import { useGetTradelinesByApplicationQuery } from '@/services/tradelines'
import { useListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { ApplicationProfile } from '@/types/unlock'
import { getLatestValue } from '@/utils'
import { applicationStates } from '@/utils/checklistGroupMappings'
import { FEATURES_LIST } from '@/utils/features/featuresList'
import { useFeatureFlag } from '@/utils/features/splitSdk'
import { scrollToElement } from '@/utils/scrollToElement'

export const DealSummaryBar = ({ application }: { application: Application }) => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { isLockedOffer } = useIsLockedOffer()
  const [showDownloadsSectionDropdown, setShowDownloadsSectionDropdown] = useState(false)
  const [showAppLinkSectionDropdown, setShowAppLinkSectionDropDown] = useState(false)
  const [showApplicationCopyIcon, setShowApplicationCopyIcon] = useState(false)
  const [showAddressCopyIcon, setShowAddressCopyIcon] = useState(false)
  const [isCopy, setIsCopy] = useState(false)
  const [isCopyingAddress, setIsCopyingAddress] = useState(false)
  const [isHoveringDownloadsSectionDropdown, setIsHoveringDownloadsSectionDropdown] =
    useState(false)
  const [isHoveringAppLinkSectionDropdown, setIsHoveringAppLinkSectionDropDown] = useState(false)
  const { data: applicationOfferCalcsResponse } = useGetApplicationOfferCalcsQuery(
    application?.uuid,
    {
      skip: !application,
    }
  )
  const applicationOfferCalcs = useMemo(
    () => applicationOfferCalcsResponse?.application?.offerCalculations,
    [applicationOfferCalcsResponse]
  )

  const { data: overrides } = useGetOverrideByApplicationQuery(application?.uuid, {
    skip: !application,
  })

  const { data: applicationCalcsResponse, isLoading: isLoadingCalcs } = useGetApplicationCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application?.externalId,
    }
  )

  const applicationCalcs = useMemo(
    () => applicationCalcsResponse?.application?.applicationCalculations,
    [applicationCalcsResponse]
  )

  const { data: applicationValuationsCalcsResponse } = useGetApplicationValuationsCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application,
    }
  )

  const applicationValuationsCalcs = useMemo(
    () => applicationValuationsCalcsResponse?.application?.valuationsCalculations,
    [applicationValuationsCalcsResponse]
  )

  const showDti =
    applicationCalcs?.calcApplicationFico < 550 &&
    application?.property?.propertyUsage !== 'investment'

  const { data: tradelinesPage } = useGetTradelinesByApplicationQuery(application?.uuid, {
    skip: !application,
  })

  const tradelines = tradelinesPage?.results

  const [isDownloadingDoc, setDownloadingDoc] = useState(false)

  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const applicantProfile = _find(applicationProfiles?.results, {
    position: 1,
  })
  const coApplicantProfile = _find(applicationProfiles?.results, {
    position: 2,
  })
  const otherProfiles: ApplicationProfile[] = applicationProfiles?.results?.filter(
    (it) => it.position > 2
  )
  const pullCreditModalOpen = useAppSelector((state) => state.general.pullCreditModalOpen)

  const avmReportModalOpen = useAppSelector((state) => state.general.avmReportModalOpen)

  const docDrawModalOpen = useAppSelector((state) => state.general.docDrawModalOpen)

  const { street, unitNumber, city, state, zipCode } = application.property.address

  const fullPropertyAddress = `${street}, ${
    unitNumber ? unitNumber : ''
  } ${city}, ${state}, ${zipCode}`

  const offer = {
    ...applicationOfferCalcs?.selectedOffer?.offers,
    ...applicationOfferCalcs?.selectedOffer,
  }

  const requestedOffer = applicationOfferCalcs?.requestedOffer

  const stageColor =
    applicationStates.find((it) => it.application_state === application?.applicationState)?.color ||
    'rgb(100,100,100)'

  const Label = ({ children }) => {
    return <p className={`text-[10px] 3xl:text-[12px]`}>{children}</p>
  }

  const Value = ({ children, ...props }) => {
    return (
      <p
        {...props}
        className={`font-bold text-[12px] 3xl:text-[14px] ${props.className || ''}`}
      >
        {children}
      </p>
    )
  }

  const ClosedLostTypeAndReason = ({
    applicantProfile,
  }: {
    applicantProfile: ApplicationProfile
  }) => {
    const { applicationState, lossReason, lossType } =
      (applicantProfile?.application as unknown as Application) ?? {}

    if (applicationState === 'closed-lost' && lossReason && lossType) {
      return (
        <p className='text-xs italic capitalize mt-1'>
          {lossType}: {lossReason.replaceAll('-', ' ')}
        </p>
      )
    }

    return null
  }

  let names = ''
  if (applicationProfiles) {
    names = `${applicantProfile.profile.full_name} ${
      coApplicantProfile ? ` & ${coApplicantProfile.profile.full_name}` : ''
    } ${otherProfiles.length ? `+ ${otherProfiles.length}` : ''}`
  }

  const namesSpan = 4

  const getValuationLabel = () => {
    const appraised = applicationValuationsCalcs.appraisalReport?.value
    const override = overrides?.home_value

    if (appraised) return 'Appraised Home Value'
    if (override) return 'AVM Value Override'
    return 'AVM Value'
  }

  const creditReport = useMemo(() => {
    if (application?.creditReports?.length > 0) {
      return getLatestValue(application.creditReports.filter((c) => !!c.reportOrderedAt))
    }
  }, [application?.creditReports])

  const getExchangeRateLabel = () => {
    const isNumber = !isNaN(overrides?.exchange_rate_adjustment as number)
    return isNumber && (overrides?.exchange_rate_adjustment as number) > 0
      ? 'Exchange Rate Adj.'
      : 'Exchange Rate'
  }

  useEffect(() => {
    setTimeout(() => setIsCopy(false), 3000)
  }, [showApplicationCopyIcon])

  const handleCopyDealName = () => {
    setIsCopy(true)
    navigator.clipboard.writeText(
      `${applicantProfile?.profile?.last_name} - ${street}, ${city}, ${state} (${window.location.href})`
    )
  }

  const handleCopyAddress = () => {
    setIsCopyingAddress(true)
    navigator.clipboard.writeText(fullPropertyAddress)
  }

  const handleClosePullCredit = () => {
    dispatch(setPullCreditModalOpen(false))
    dispatch(setPullCreditModalUser(''))
    dispatch(setPullCreditModalAction(''))
  }

  const handleCloseAvmReportModal = () => {
    dispatch(setAvmReportModalOpen(false))
  }

  const getOtherApplicants = () => {
    const otherApplicants = []
    if (coApplicantProfile) otherApplicants.push(coApplicantProfile)
    otherApplicants.push(...otherProfiles)
    return otherApplicants
  }

  const viewInCX = (e) => {
    const cxBaseUrl =
      process.env.NEXT_PUBLIC_CX_URL ||
      `${window.location.host.replace('max.', 'app.').replace('max2.', 'app2.')}`

    const token = localStorage.getItem('jwt')

    const url = `${cxBaseUrl}/ops-read-only?token=${token}&uuid=${application.uuid}`

    window.open(url, '_blank').focus()
  }

  const gridClassnames = classNames(
    {
      'grid-cols-10': showDti,
      'grid-cols-9': !showDti,
    },
    'sticky top-0 h-48 w-full border-b-2 border-gray-200 bg-white z-20 grid gap-3 px-6 py-3'
  )

  const isGlobalAuditModalEnabled = useFeatureFlag(FEATURES_LIST.GLOBAL_AUDIT_MODAL)

  return (
    <div className={gridClassnames}>
      <div style={{ gridColumn: `span ${namesSpan}` }}>
        <div>
          <Label>Applicant(s)</Label>
          <div
            className={`font-bold whitespace-nowrap text-ellipsis overflow-hidden text-[28px] flex flex-row`}
            data-test='summary-applicants'
            onMouseEnter={() => setShowApplicationCopyIcon(true)}
            onMouseLeave={() => setShowApplicationCopyIcon(false)}
          >
            <span
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                scrollToElement(`/applications/${router.query.id}`, 'personal-info', router)
              }}
            >
              {names}
            </span>
            <LinkIcon
              onClick={() => handleCopyDealName()}
              data-tip
              data-for='copyDealName'
              className={`w-5 ml-3 text-gray-400 transition-all ease-in-out duration-700 ${
                !showApplicationCopyIcon && `hidden`
              } cursor-pointer outline-none`}
            />
            <ReactTooltip
              place={'right'}
              id='copyDealName'
            >
              {isCopy ? `Copied!` : `Copy`}
            </ReactTooltip>
          </div>
        </div>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => scrollToElement(`/applications/${router.query.id}`, 'property', router)}
      >
        <Label>Agreement #</Label>
        <Value className='mt-3'>{application.externalId}</Value>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() =>
          scrollToElement(`/applications/${router.query.id}/summary`, 'credit', router)
        }
      >
        <Label>Application {creditReport?.isVantage ? 'VScore' : 'FICO'}</Label>
        <Value
          data-test='summary-fico'
          className='mt-3'
        >
          <NumberFormat
            value={applicationCalcs?.calcApplicationFico}
            displayType='text'
            decimalScale={0}
          />
        </Value>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() =>
          scrollToElement(
            `/applications/${router.query.id}/underwriting`,
            'investment-estimate',
            router
          )
        }
      >
        <Label>Est. Closing Date</Label>
        <Value
          data-test='summary-estimated-close'
          className='mt-3'
        >
          {application.estimatedCloseOn
            ? formatDate(new Date(application.estimatedCloseOn.replace(/-/g, '/')), 'MM/dd/yyyy')
            : '-'}
        </Value>
      </div>
      {showDti && (
        <div
          id='dti'
          style={{ gridColumn: `span ${5 - namesSpan}`, width: '5rem' }}
          data-for='dti'
          data-tip
          className='hover:cursor-pointer'
          onClick={() => dispatch(setViewTradelinesModalOpen(true))}
        >
          <Label>DTI</Label>
          <Value
            data-test='summary-dti'
            className='mt-3'
          >
            {applicationCalcs && applicationCalcs?.dti !== null && tradelines?.length != 0 ? (
              <>
                {(applicationCalcs?.dti * 100).toFixed(2)}
                <span>%</span>
              </>
            ) : (
              'TBD'
            )}
          </Value>
          {tradelines?.length == 0 && (
            <ReactTooltip
              id='dti'
              place='bottom'
            >
              Import tradelines to calculate it
            </ReactTooltip>
          )}
        </div>
      )}
      {/* Download section */}
      {/* TODO: Remove peer-data-* once Deal Grade is working */}
      <div className='flex gap-5 justify-end peer-data-[deal-grade-visible=false]:col-span-2'>
        {isGlobalAuditModalEnabled && <DealAudit />}
        <div
          className='tooltip tooltip-left'
          data-tip='CX View'
        >
          <div
            className='bg-gray-400 rounded-full w-7 h-7 flex justify-center items-center cursor-pointer'
            onClick={viewInCX}
          >
            <ArrowTopRightOnSquare
              width={5}
              className='-mt-[3px] fill-indigo-900'
            />
          </div>
        </div>
        <DropdownButton
          icon={
            <ArrowDownTrayIcon
              width={22}
              className={`${isLockedOffer ? `text-slate-400` : `text-indigo-900`}`}
            />
          }
          isLocked={isLockedOffer}
          loading={isDownloadingDoc}
          position={2}
          showDropdown={showDownloadsSectionDropdown}
          setShowDropdown={setShowDownloadsSectionDropdown}
        >
          <div
            className='tooltip tooltip-left'
            data-tip={!isHoveringDownloadsSectionDropdown ? 'Download to MAX' : null}
          >
            <DownloadsSection
              downloadingDoc={(downloading) => setDownloadingDoc(downloading)}
              showDropdown={showDownloadsSectionDropdown}
              setShowDropdown={setShowDownloadsSectionDropdown}
              setIsHovering={setIsHoveringDownloadsSectionDropdown}
            />
          </div>
        </DropdownButton>
        <div
          className='tooltip tooltip-left'
          data-tip={!isHoveringAppLinkSectionDropdown ? 'Quick Links' : null}
        >
          <DropdownButton
            icon={
              <LinkIcon
                width={22}
                className='text-indigo-900'
              />
            }
            loading={false}
            position={1}
            showDropdown={showAppLinkSectionDropdown}
            setShowDropdown={setShowAppLinkSectionDropDown}
          >
            <AppLinkSection
              application={application}
              setIsHovering={setIsHoveringAppLinkSectionDropDown}
            />
          </DropdownButton>
        </div>
      </div>
      <div
        className={`${showDti ? 'col-span-3' : 'col-span-2'}`}
        onMouseEnter={() => setShowAddressCopyIcon(true)}
        onMouseLeave={() => {
          setShowAddressCopyIcon(false)
          setIsCopyingAddress(false)
        }}
      >
        <Label>{PROPERTY_USAGES?.[application?.property?.propertyUsage]}</Label>
        <div className='flex flex-row items-center'>
          <Value
            className='flex mt-3'
            data-test='summary-property-address'
            style={{ cursor: 'pointer' }}
            onClick={() => scrollToElement(`/applications/${router.query.id}`, 'property', router)}
          >
            {fullPropertyAddress}
          </Value>
          {showAddressCopyIcon && (
            <DocumentDuplicateIcon
              data-tip
              data-for='copyPropertyAddress'
              width={26}
              onClick={() => handleCopyAddress()}
              color={isCopyingAddress ? 'grey' : null}
              className='cursor-pointer'
            />
          )}
          <ReactTooltip
            id='copyPropertyAddress'
            place='right'
          >
            Copy
          </ReactTooltip>
        </div>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => {
          if (getValuationLabel() === 'AVM Value') {
            scrollToElement(`/applications/${router.query.id}/avm`, 'atypicality', router)
          } else if (getValuationLabel() === 'AVM Value Override') {
            scrollToElement(`/applications/${router.query.id}/summary`, 'assets', router)
          } else if (getValuationLabel() === 'Appraised Home Value') {
            scrollToElement(
              `/applications/${router.query.id}/underwriting`,
              'appraisal-report',
              router
            )
          }
        }}
      >
        <Label>{getValuationLabel()}</Label>
        <Value className='mt-3'>
          <CustomNumberFormat
            displayType='text'
            prefix='$'
            thousandSeparator={true}
            decimalScale={2}
            value={applicationValuationsCalcs?.propertyEstimatedValue}
            data-test='summary-estimated-value'
          />
        </Value>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => scrollToElement(`/applications/${router.query.id}/summary`, 'offer', router)}
      >
        <Label>Unlock Investment</Label>
        <Value className='mt-3'>
          {offer ? (
            <CustomNumberFormat
              prefix='$'
              thousandSeparator={true}
              value={offer.offerInvestment}
              displayType='text'
              decimalScale={0}
              roundingSystem='down'
              data-test='summary-offer-investment'
            />
          ) : (
            <>-</>
          )}
        </Value>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => scrollToElement(`/applications/${router.query.id}/summary`, 'offer', router)}
      >
        <Label>{getExchangeRateLabel()}</Label>
        <Value
          data-test='summary-exchange-rate'
          className='mt-3'
        >
          {offer ? (
            <>
              {offer.exchangeRate}
              <span className='text-xs'>X</span>
            </>
          ) : (
            <>-</>
          )}
        </Value>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => scrollToElement(`/applications/${router.query.id}/summary`, 'offer', router)}
      >
        <Label>ACL</Label>
        <Value
          data-test='summary-acl'
          className='mt-3'
        >
          <NumberFormat
            value={parseFloat((applicationOfferCalcs?.annualizedCostLimit * 100).toFixed(5))}
            displayType={'text'}
            suffix={'%'}
            thousandSeparator={true}
            decimalScale={2}
          />
        </Value>
      </div>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() =>
          scrollToElement(`/applications/${router.query.id}/summary`, 'capital', router)
        }
      >
        {!offer ? (
          <>
            <Label>
              {applicationOfferCalcs?.selectedOffer?.error ? 'THFL' : 'THF'} / LTV / CLTV
            </Label>
            <Value
              data-test='summary-thf-ltv'
              className='mt-3'
            >
              <NumberFormat
                value={
                  applicationOfferCalcs?.selectedOffer?.error
                    ? offer?.totalHomeFinance
                    : requestedOffer?.thfLimit
                }
                displayType='text'
                decimalScale={2}
              />
              {' / '}
              <NumberFormat
                value={offer?.ltv ? offer?.ltv * 100 : 0}
                displayType='text'
                decimalScale={2}
              />
              {' / '}
              <NumberFormat
                value={offer?.cltv ? offer?.cltv * 100 : 0}
                displayType='text'
                decimalScale={2}
              />
            </Value>
          </>
        ) : (
          <>-</>
        )}
      </div>
      <div className='col-span-2'>
        <div className='pl-[24px]'>
          <Label>Current Sub Stage</Label>
        </div>
        <div className='flex gap-2 items-start mt-3'>
          <div
            className='w-[22px] h-[22px]'
            style={{ background: stageColor }}
          />
          <div>
            <StageSelector application={application} />
            <ClosedLostTypeAndReason applicantProfile={applicantProfile} />
          </div>
        </div>
      </div>
      {pullCreditModalOpen && (
        <PullCreditModal
          applicationId={application?.uuid}
          applicant={applicantProfile}
          otherApplicants={getOtherApplicants()}
          application={application}
          onClose={handleClosePullCredit}
        />
      )}
      {avmReportModalOpen && <AvmReportModal onClose={handleCloseAvmReportModal} />}
      <DocDrawModal application={application} />
      <ViewTradelinesModal
        application={application}
        calcs={applicationCalcs}
        isLoadingCalcs={isLoadingCalcs}
      />
    </div>
  )
}
